import { isPerfMetricsEnabled } from '../../lib/debug/performance';
import SubscriptionTimingTracker from '../../lib/store-util/SubscriptionTimingTracker';

import styles from './SubscriptionTimingDebug.module.scss';

const SLOW_THRESHOLD_MS = 1000;
const CLOCK_PAD_SIZE = 2;
const MILLIS_PAD_SIZE = 3;

const formatClock = (timestamp: number): string => {
  const date = new Date(timestamp);
  const pad = (value: number, size: number): string => String(value).padStart(size, '0');
  const time = [ date.getHours(), date.getMinutes(), date.getSeconds() ]
    .map(value => pad(value, CLOCK_PAD_SIZE))
    .join(':');
  return `${time}.${pad(date.getMilliseconds(), MILLIS_PAD_SIZE)}`;
};

type TimingRowProps = {
  label: string;
  sentAt: number;
  receivedAt: number | null;
};

const TimingRow = ({ label, sentAt, receivedAt }: TimingRowProps) => {
  if (receivedAt == null) {
    return (
      <div className={styles.row}>
        <span className={styles.label}>{label}</span>
        <span className={styles.pending}>waiting…</span>
      </div>
    );
  }

  const diffMs = receivedAt - sentAt;

  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>
        {formatClock(receivedAt)}
        <span className={diffMs >= SLOW_THRESHOLD_MS ? styles.diffSlow : styles.diff}>{`+${diffMs}ms`}</span>
      </span>
    </div>
  );
};

/**
 * Round-trip timing of the current watchlist subscription batch: when the subscribe request went
 * out, and when the first chart and first price responses arrived. Restarts whenever a differently
 * labelled batch is sent, so switching watchlist tabs starts a new measurement.
 */
const SubscriptionTimingDebug = () => {
  const {
    label, sentAt, chartAt, priceAt,
  } = SubscriptionTimingTracker.use();

  if (!isPerfMetricsEnabled() || sentAt == null) {
    return null;
  }

  return (
    <div className={styles.panel}>
      <div className={styles.title}>
        <span>{`${label} subscription`}</span>
        <span>timings</span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>Sent at</span>
        <span className={styles.value}>{formatClock(sentAt)}</span>
      </div>
      <TimingRow label="Chart response" sentAt={sentAt} receivedAt={chartAt} />
      <TimingRow label="Price response" sentAt={sentAt} receivedAt={priceAt} />
    </div>
  );
};

export default SubscriptionTimingDebug;
